import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
}

const AuthLayout = ({ children, title, subtitle, showBackButton = false }: AuthLayoutProps) => {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Identity band */}
      <div className="relative bg-primary-950 overflow-hidden">
        <img src="/images/sv-skyline-evening.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-primary-950/55" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-7 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-white/95 flex items-center justify-center overflow-hidden">
              <img src="/images/logo.png" alt="Sky View GmbH" className="h-6 w-auto" />
            </div>
            <span className="font-serif text-lg text-white tracking-wide">Sky View</span>
          </Link>

          {showBackButton ? (
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-primary-200 hover:text-gold-400 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Zur Startseite
            </Link>
          ) : (
            <div className="hidden sm:flex items-center gap-2 text-primary-200 text-xs uppercase tracking-[0.14em]">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500" />
              Sicherer Bereich
            </div>
          )}
        </div>
      </div>

      {/* Form area */}
      <div className="flex-1 flex items-center justify-center px-6 py-14 md:py-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-600 mb-3">Sky View Portal</div>
            <h1 className="font-serif text-3xl text-slate-900 mb-2 leading-tight">{title}</h1>
            {subtitle && <p className="text-slate-500">{subtitle}</p>}
          </div>

          <div className="bg-white border border-slate-200 border-t-[3px] border-t-primary-600 px-7 py-9 md:px-9 md:py-10">
            {children}
          </div>

          <div className="mt-6 grid grid-cols-3 divide-x divide-slate-200 border border-slate-200 bg-white">
            <div className="px-3 py-4 text-center">
              <div className="font-serif text-base text-primary-800">62+</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">Partnerbanken</div>
            </div>
            <div className="px-3 py-4 text-center">
              <div className="font-serif text-base text-primary-800">EU</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">Einlagensicherung</div>
            </div>
            <div className="px-3 py-4 text-center">
              <div className="font-serif text-base text-primary-800">24/7</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">Kontozugriff</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 py-6 text-center">
        <p className="text-xs text-slate-400">© 2026 Sky View GmbH. Alle Rechte vorbehalten.</p>
      </div>
    </div>
  );
};

export default AuthLayout;
